import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, WarningCircle, ArrowRight, EnvelopeSimple, Buildings, UserPlus } from "@phosphor-icons/react";
import { acceptInvite, fetchInviteDetails } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { getErrorMessage } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "One number", test: (p) => /[0-9]/.test(p) },
];

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginSuccess, user } = useAuth();
  const token = searchParams.get("token") || "";

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ full_name: "", password: "", confirmPassword: "" });

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("This invitation link is missing or invalid.");
      return;
    }
    fetchInviteDetails(token)
      .then(setInvite)
      .catch((err) => setError(getErrorMessage(err, "This invitation is no longer valid.")))
      .finally(() => setLoading(false));
  }, [token]);

  const passwordValid = useMemo(
    () => PASSWORD_RULES.every((rule) => rule.test(form.password)),
    [form.password],
  );
  const passwordsMatch = form.password === form.confirmPassword && form.confirmPassword.length > 0;

  const handleAccept = async (event) => {
    event.preventDefault();
    setError("");
    if (!passwordValid) {
      setError("Password does not meet the requirements.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }
    setAccepting(true);
    try {
      const userData = await acceptInvite(token, {
        full_name: form.full_name,
        password: form.password,
      });
      loginSuccess(userData);
      navigate("/dashboard");
    } catch (err) {
      setError(getErrorMessage(err, "Could not accept this invitation."));
    } finally {
      setAccepting(false);
    }
  };

  const loginLink = invite
    ? `/login?invite_token=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email)}&org=${encodeURIComponent(invite.org_name)}`
    : "/login";

  return (
    <div className="min-h-screen flex" data-testid="accept-invite-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm space-y-8">
          <BrandLogo
            iconClassName="w-10 h-10 rounded-lg"
            textClassName="text-2xl font-extrabold tracking-tight font-heading text-slate-900"
          />

          {loading ? (
            <div className="space-y-3">
              <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Checking your invite…</h1>
              <p className="text-sm text-slate-500">Loading invitation details.</p>
            </div>
          ) : error ? (
            <div className="space-y-6">
              <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                <WarningCircle weight="fill" className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
              <Link to="/login" className="flex h-11 items-center justify-center rounded-md bg-black text-sm font-bold text-white hover:bg-primary/90">
                Back to sign in
              </Link>
            </div>
          ) : invite?.already_member ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">You already have access</h1>
                <p className="mt-2 text-sm text-slate-500">
                  {invite.email} is already a member of {invite.org_name}. Sign in to continue.
                </p>
              </div>
              <Link to="/login" className="flex h-11 items-center justify-center rounded-md bg-black text-sm font-bold text-white hover:bg-primary/90">
                Go to sign in
              </Link>
            </div>
          ) : invite?.existing_user ? (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Sign in to accept invite</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {invite.email} already has a UteFlow account. Sign in with that account and we’ll add you to {invite.org_name}.
                </p>
              </div>
              <div className="space-y-3 rounded-md border border-black/10 bg-foreground/5 p-4 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <Buildings className="h-4 w-4" weight="duotone" />
                  <span>{invite.org_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <EnvelopeSimple className="h-4 w-4" weight="duotone" />
                  <span>{invite.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" weight="duotone" />
                  <span>{invite.role} access</span>
                </div>
              </div>
              <Link to={loginLink} className="flex h-11 items-center justify-center gap-2 rounded-md bg-black text-sm font-bold text-white hover:bg-primary/90">
                Sign in and join
                <ArrowRight className="h-4 w-4" weight="bold" />
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Accept your invitation</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Create your account to join {invite?.org_name} in UteFlow as {invite?.role}.
                </p>
              </div>

              <div className="space-y-3 rounded-md border border-black/10 bg-foreground/5 p-4 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <Buildings className="h-4 w-4" weight="duotone" />
                  <span>{invite?.org_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <EnvelopeSimple className="h-4 w-4" weight="duotone" />
                  <span>{invite?.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" weight="duotone" />
                  <span>{invite?.role} access</span>
                </div>
              </div>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleAccept} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Full Name</label>
                  <input
                    data-testid="accept-invite-name"
                    required
                    value={form.full_name}
                    onChange={(e) => setForm((current) => ({ ...current, full_name: e.target.value }))}
                    className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
                  <input
                    data-testid="accept-invite-password"
                    type="password"
                    required
                    value={form.password}
                    onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))}
                    className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black"
                  />
                  {form.password.length > 0 && (
                    <ul className="mt-2.5 space-y-1">
                      {PASSWORD_RULES.map((rule) => {
                        const passed = rule.test(form.password);
                        return (
                          <li key={rule.label} className={`flex items-center gap-1.5 text-xs font-medium ${passed ? "text-emerald-600" : "text-slate-400"}`}>
                            <CheckCircle weight={passed ? "fill" : "regular"} className="h-3.5 w-3.5 flex-shrink-0" />
                            {rule.label}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Confirm Password</label>
                  <input
                    data-testid="accept-invite-confirm"
                    type="password"
                    required
                    value={form.confirmPassword}
                    onChange={(e) => setForm((current) => ({ ...current, confirmPassword: e.target.value }))}
                    className="w-full h-12 px-4 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black"
                  />
                </div>
                <button
                  data-testid="accept-invite-submit"
                  type="submit"
                  disabled={accepting || !passwordValid || !passwordsMatch}
                  className="w-full h-12 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {accepting ? "Joining..." : "Create account and join"}
                </button>
              </form>

              <p className="text-sm text-slate-500 text-center">
                Already have an account? <Link to={loginLink} className="text-foreground font-bold hover:underline">Sign in instead</Link>
              </p>
            </div>
          )}

          {user && user !== false && (
            <p className="text-xs text-slate-400">
              You are already signed in. If this invite belongs to another email, sign out first.
            </p>
          )}
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="relative z-10 text-center space-y-6 max-w-md">
          <UserPlus weight="duotone" className="w-24 h-24 text-foreground mx-auto" />
          <h2 className="text-3xl font-black tracking-tight font-heading text-white">Join the team workspace</h2>
          <p className="text-slate-400 text-base font-medium leading-relaxed">
            Invitations bring staff into the same rental, fleet, agreement and invoicing workflow without sharing logins.
          </p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
      </div>
    </div>
  );
}
